// crypto.randomUUID() only exists in a SECURE context (https / localhost). When the
// tablet shell loads the dev server over http:// on a LAN IP, the context is insecure
// and crypto.randomUUID is undefined, crashing every caller (useCart, device-id, etc.).
// Polyfill it here — instrumentation-client runs before app code — using
// crypto.getRandomValues, which IS available over http. No-op in prod (https), where
// the native randomUUID exists. (Dev-server-on-device enabler; not part of the PAX fix.)
if (typeof crypto !== "undefined" && typeof crypto.randomUUID !== "function") {
  const polyfill = (): `${string}-${string}-${string}-${string}-${string}` => {
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant 10
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };
  try {
    Object.defineProperty(crypto, "randomUUID", { configurable: true, writable: true, value: polyfill });
  } catch {
    try { (crypto as { randomUUID?: typeof polyfill }).randomUUID = polyfill; } catch { /* noop */ }
  }
}

// ---------------------------------------------------------------------------
// Vercel BotID — CAPTCHA invisible del registro público.
//
// `initBotId` hace que el navegador resuelva un desafío y adjunte sus cabeceras
// a los POST de las rutas listadas aquí. Sin esta declaración, `checkBotId()`
// en el servidor clasifica como bot a TODO el mundo — así que esta lista y las
// rutas que llaman a `checkSignupBotId()` (lib/server/botid.ts) se mueven
// juntas o no se mueven. Hay un test que amarra ese par.
//
// El script del desafío se sirve MISMO-ORIGEN (withBotId reescribe /149e9513-…/
// hacia api.vercel.com), así que pasa el `script-src 'self'` de la CSP estricta
// sin abrirle hosts nuevos a nadie.
//
// Solo `/api/auth/signup`: es donde un bot cuesta dinero real — ensucia la cola
// de aprobaciones y quema reputación de envío de correo. Los heartbeats de POS
// y pantallas NO van aquí a propósito; ver el comentario en lib/server/botid.ts.
// ---------------------------------------------------------------------------
import { initBotId } from "botid/client/core"

initBotId({
  protect: [{ path: "/api/auth/signup", method: "POST" }],
})

// ---------------------------------------------------------------------------
// Sentry — SOLO CLIENTE (navegador / WebView de las tablets).
//
// Este archivo lo carga Next.js core en el bundle del cliente
// (instrumentation-client es un mecanismo de Next, no del build plugin de
// Sentry), así que este init NO toca chunks del servidor y no puede
// reproducir el incidente P1 de 2026-06-16 (#446/#453: el wrapper server de
// @sentry/nextjs + Next 16 + Turbopack colgaba TODAS las funciones
// serverless en cold start → el POS no podía cobrar; sigue roto upstream,
// getsentry/sentry-javascript#19367 cerrado "not planned").
//
// El init de SERVIDOR se reactivó el 2026-07-18 (instrumentation.ts), solo
// runtime — withSentryConfig sigue fuera de next.config.ts a propósito.
//
// Requiere NEXT_PUBLIC_SENTRY_DSN en el env de Vercel (sin DSN el SDK queda
// deshabilitado) y el dominio *.sentry.io en el connect-src del CSP
// (next.config.ts).
//
// C-19: never let customer phones, emails, PINs, or card data leave the
// app. Scrub at the boundary on every event and every breadcrumb.
// ---------------------------------------------------------------------------
import * as Sentry from "@sentry/nextjs"
import { scrubPiiBreadcrumb } from "@/lib/sentry/pii-scrubber"
import { sentryBeforeSend } from "@/lib/sentry/beforeSend"
import { sentryTracesSampler } from "@/lib/sentry/tracesSampler"

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  // Muestreo por ruta — ver lib/sentry/tracesSampler.ts. En el cliente el nombre
  // de transacción llega sin verbo HTTP ("/[slug]/pos"); el sampler lo maneja.
  // Los ERRORES siguen al 100%: `ignoreErrors`/`denyUrls`/`beforeSend` de abajo
  // son los únicos que filtran eventos, y esto no los toca.
  tracesSampler: sentryTracesSampler,
  enabled: !!process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.NODE_ENV,
  // `release` y `dist` — sin esto los eventos de CLIENTE llegan SIN versión, y no
  // se puede distinguir "el bug sigue vivo" de "esa tablet corre un bundle viejo".
  // Eso fue exactamente lo que volvió indecidible el triage del 2026-07-20: los
  // RangeError de /pos no traían versión y hubo que razonarlo por el service
  // worker y el modelo de tablet.
  //
  // El SERVIDOR sí trae release: el SDK lo auto-detecta de VERCEL_GIT_COMMIT_SHA,
  // que existe en Node. En el browser esa variable NO entra al bundle, y aquí el
  // build plugin de Sentry (que normalmente lo inyecta) está desactivado a
  // propósito — withSentryConfig sigue fuera de next.config.ts por el P1 de
  // #446/#453. Así que se setea a mano desde las system env vars que Vercel sí
  // expone al cliente; es el MISMO mecanismo del que ya depende `environment`
  // arriba con NEXT_PUBLIC_VERCEL_ENV, así que si aquél funciona, éstos también.
  //
  //  - `release` = git SHA → el MISMO valor que reporta el servidor, así ambos
  //    lados caen bajo un solo release en Sentry en vez de dos.
  //  - `dist` = deployment id → el MISMO que devuelve /api/build-id, que es lo que
  //    consulta el BuildWatcher. Comparar el `dist` de un evento contra
  //    /api/build-id responde "¿esta tablet está en el bundle viejo?" sin adivinar.
  //
  // Ambas son opcionales: en local quedan undefined y el SDK simplemente omite el
  // campo.
  release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  dist: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID,
  // Drop version-skew, then scrub PII. See lib/sentry/beforeSend.ts.
  beforeSend: sentryBeforeSend,
  beforeBreadcrumb: scrubPiiBreadcrumb,
  // Third-party / network noise that reaches this client init but is NOT a Redy
  // defect. Filtered here (InboundFilters) so the Sentry stream reflects real
  // product bugs. Matched as substrings/regex against the error message.
  ignoreErrors: [
    // Public storefronts (/order/[slug]) get opened inside in-app browsers
    // (Instagram, Facebook, TikTok, LinkedIn) and behind extensions / password
    // managers that inject their own scripts assuming an iOS WKWebView JS→native
    // bridge. When that bridge isn't registered, `window.webkit.messageHandlers.X`
    // is undefined and THEIR script throws. Not our code: the repo has zero
    // `webkit` / `messageHandlers` references.
    /window\.webkit\.messageHandlers/i,
    "messageHandlers",
    // Aborted or failed client fetches on flaky mobile networks, or when the user
    // navigates away mid-request. Safari: "Load failed"; Chrome: "Failed to
    // fetch"; Firefox: "NetworkError…". Irreducible noise floor of any page that
    // fetches — not actionable per event.
    "Load failed",
    "Failed to fetch",
    "NetworkError when attempting to fetch resource",
    "Network request failed",
    "The network connection was lost",
    "The Internet connection appears to be offline",
    "AbortError",
    // The browser recovers from these itself; never a real defect.
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
  ],
  // Drop any event whose top stack frame lives in an injected/extension script
  // rather than our bundle — the actual origin of the webkit crash above.
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-web-extension:\/\//i,
    /^safari-extension:\/\//i,
    // Safari masks scripts injected by in-app browsers and extensions behind
    // this scheme.
    /^webkit-masked-url:/i,
    // Android in-app browsers (Instagram, Facebook) inject a
    // `navigation_performance_logger_android` script that postMessages to a
    // native Java bridge on `beforeunload`. When the WebView is tearing down the
    // bridge object is already GC'd, so THEIR script throws "Error invoking
    // postMessage: Java object is gone". Matched by the injected script's origin
    // (not the message) so a genuine Java-bridge failure from OUR tablet shell —
    // which would surface from our own bundle — is never masked.
    /navigation_performance_logger/i,
  ],
})
